import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Lock, BellRing, Eye, CreditCard, LogOut, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";

const TABS = [
  { value: "profile", label: "PROFILE", icon: User },
  { value: "account", label: "ACCOUNT", icon: Lock },
  { value: "preferences", label: "PREFERENCES", icon: BellRing },
  { value: "privacy", label: "PRIVACY", icon: Eye },
  { value: "membership", label: "MEMBERSHIP", icon: CreditCard },
];

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // editable
  const [name, setName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [dealership, setDealership] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [audience, setAudience] = useState("automotive");
  const [audiences, setAudiences] = useState([]);
  const [busyProfile, setBusyProfile] = useState(false);

  // password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busyPw, setBusyPw] = useState(false);

  // prefs
  const [prefs, setPrefs] = useState({
    notifications_mentions: true,
    notifications_comments: true,
    notifications_reactions: true,
    notifications_team_new_post: false,
    notifications_achievements: true,
    notifications_system: true,
    notifications_weekly_recap: true,
  });
  const [privacy, setPrivacy] = useState({ show_alias_on_leaderboard: true });

  const load = async () => {
    try {
      const [r, aRes] = await Promise.all([
        api.get("/settings/profile"),
        api.get("/audiences").catch(() => ({ data: { audiences: [] } })),
      ]);
      setProfile(r.data);
      setName(r.data.name || "");
      setJobTitle(r.data.job_title || "");
      setDealership(r.data.dealership_name || "");
      setCity(r.data.city || "");
      setStateCode((r.data.state || "").toUpperCase());
      setAudience(r.data.audience || "automotive");
      setAudiences(aRes.data.audiences || []);
      setPrefs(r.data.preferences || prefs);
      setPrivacy(r.data.privacy || privacy);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusyProfile(true);
    try {
      await api.put("/settings/profile", {
        name,
        job_title: jobTitle,
        dealership_name: dealership,
        city: city || null,
        state: stateCode ? stateCode.toUpperCase() : null,
      });
      // Audience is updated via the dedicated /users/me endpoint
      if (audience !== profile.audience) {
        await api.patch("/users/me", { audience });
      }
      toast.success("Profile updated");
      const me = await api.get("/auth/me").then(r => r.data); setUser(me);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusyProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("New password must be 6+ chars"); return; }
    setBusyPw(true);
    try {
      await api.post("/settings/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed. Other devices signed out.");
      setCurrentPw(""); setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusyPw(false); }
  };

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out of every device, including this one?")) return;
    try {
      await api.post("/settings/sign-out-everywhere");
      await logout();
      navigate("/login");
    } catch (e) { console.error(e); toast.error("Failed"); }
  };

  const savePrefs = async () => {
    try {
      await api.put("/settings/preferences", prefs);
      toast.success("Preferences saved");
    } catch (e) { console.error(e); toast.error("Failed"); }
  };

  const savePrivacy = async () => {
    try {
      await api.put("/settings/privacy", privacy);
      toast.success("Privacy saved");
    } catch (e) { console.error(e); toast.error("Failed"); }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-fc-navy flex" data-surface="light">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fc-navy flex" data-surface="light" data-testid="settings-page">
      <Sidebar />
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        <div className="h-20 px-6 lg:px-10 flex items-center border-b border-gray-200 bg-white/70 backdrop-blur sticky top-0 z-30">
          <div>
            <p className="fc-overline text-[10px]">ACCOUNT</p>
            <h1 className="text-lg sm:text-xl font-black tracking-tight">Settings</h1>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-8 grid lg:grid-cols-[220px_1fr] gap-8 max-w-5xl mx-auto">
          <nav className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setTab(t.value)}
                  className={`flex items-center gap-3 px-3 py-2.5 text-xs tracking-[0.18em] uppercase font-bold transition-colors ${active ? "bg-fc-red/15 text-fc-red border-l-2 border-fc-red" : "text-gray-500 hover:text-gray-900"}`}
                  data-testid={`settings-tab-${t.value}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </nav>

          <div className="space-y-6">
            {tab === "profile" && (
              <form onSubmit={saveProfile} className="bg-white border border-gray-200 p-6 space-y-4" data-testid="profile-form">
                <p className="fc-overline text-[10px]">PROFILE</p>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Display name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red" data-testid="settings-name" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Email (read-only)</label>
                  <input value={profile.email} disabled className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm text-gray-500" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Job title</label>
                    <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red" data-testid="settings-job-title" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Dealership</label>
                    <input value={dealership} onChange={(e) => setDealership(e.target.value)} className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red" data-testid="settings-dealership" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Public alias</label>
                  <p className="font-mono text-fc-red font-bold text-base">{profile.public_alias}</p>
                  <p className="text-[10px] tracking-wider text-gray-400 mt-1">Used on leaderboards & community. Cannot be changed.</p>
                </div>
                {audiences.length > 0 && (
                  <div data-testid="settings-audience-block">
                    <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Audience track</label>
                    <select
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red"
                      data-testid="settings-audience"
                    >
                      {audiences.map((a) => (
                        <option key={a.slug} value={a.slug}>{a.accent_emoji} {a.label}</option>
                      ))}
                    </select>
                    <p className="text-[10px] tracking-wider text-gray-400 mt-2">
                      This controls which courses we recommend first across your dashboard and catalog.
                    </p>
                  </div>
                )}
                <button disabled={busyProfile} className="btn-fc-red" data-testid="profile-save">
                  {busyProfile ? "Saving…" : "Save Profile"}
                </button>
              </form>
            )}

            {tab === "profile" && profile && (
              <div className="bg-white border border-gray-200 p-6 space-y-4 mt-6" data-testid="location-form">
                <p className="fc-overline text-[10px]">LOCATION (leaderboard display)</p>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Used to label your row on the anonymized leaderboard. Dealers see your city (e.g. <span className="font-mono text-gray-600">"Phoenix Dealer · AZ"</span>); operators see your initials (e.g. <span className="font-mono text-gray-600">"A.C. · FL"</span>). Optional — leave blank to show a dash.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
                  <div>
                    <label className="block text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500 mb-1.5">City</label>
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Phoenix"
                      maxLength={80}
                      className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red"
                      data-testid="location-city"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500 mb-1.5">State</label>
                    <input
                      value={stateCode}
                      onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="AZ"
                      maxLength={2}
                      className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm uppercase tracking-wider focus:outline-none focus:border-fc-red"
                      data-testid="location-state"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={saveProfile}
                  disabled={busyProfile}
                  className="btn-fc-red"
                  data-testid="location-save"
                >
                  {busyProfile ? "Saving…" : "Save location"}
                </button>
              </div>
            )}

            {tab === "account" && (
              <>
                <form onSubmit={changePassword} className="bg-white border border-gray-200 p-6 space-y-4" data-testid="password-form">
                  <p className="fc-overline text-[10px]">CHANGE PASSWORD</p>
                  <input
                    type="password" placeholder="Current password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                    className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red"
                    data-testid="current-password"
                  />
                  <input
                    type="password" placeholder="New password (min 6 chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    className="w-full bg-white border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red"
                    data-testid="new-password"
                  />
                  <p className="text-[10px] tracking-wider text-gray-400">Changing your password signs all other devices out.</p>
                  <button disabled={busyPw} className="btn-fc-red" data-testid="password-save">
                    {busyPw ? "Updating…" : "Update password"}
                  </button>
                </form>

                <div className="bg-white border border-fc-red/30 p-6">
                  <p className="fc-overline text-[10px]">SESSIONS</p>
                  <p className="text-gray-600 text-sm mt-3 mb-4 leading-relaxed">
                    Forcibly invalidate every session — including this one. You&apos;ll need to log back in.
                  </p>
                  <button onClick={signOutEverywhere} className="btn-fc-outline text-xs" data-testid="signout-everywhere">
                    <LogOut size={12} /> Sign out everywhere
                  </button>
                </div>
              </>
            )}

            {tab === "preferences" && (
              <div className="bg-white border border-gray-200 p-6 space-y-1" data-testid="preferences-form">
                <p className="fc-overline text-[10px]">NOTIFICATIONS</p>
                <p className="text-gray-500 text-[11px] tracking-wide leading-relaxed pb-3">
                  Choose which events trigger a notification in your inbox.
                </p>
                {[
                  { k: "notifications_mentions",       label: "@Mentions in Community / Internal", hint: "When someone tags you with @" },
                  { k: "notifications_comments",       label: "Comments on my posts",              hint: "Community + Team feeds" },
                  { k: "notifications_reactions",      label: "Reactions on my posts",             hint: "Grouped into one notification per hour" },
                  { k: "notifications_team_new_post",  label: "New posts in my team feed",         hint: "Off by default — opt in to follow every post" },
                  { k: "notifications_achievements",   label: "Badge & rank achievements",         hint: "When you earn a badge or rank up" },
                  { k: "notifications_system",         label: "System announcements",              hint: "Maintenance, security, billing alerts" },
                  { k: "notifications_weekly_recap",   label: "Weekly progress recap",             hint: "Coming soon — your week summarised" },
                ].map(opt => (
                  <label key={opt.k} className="flex items-start justify-between cursor-pointer py-3 border-b border-gray-100 last:border-b-0 gap-4">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm">{opt.label}</span>
                      <span className="block text-[11px] tracking-wide text-gray-400 mt-0.5">{opt.hint}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={!!prefs[opt.k]}
                      onChange={(e) => setPrefs(p => ({ ...p, [opt.k]: e.target.checked }))}
                      className="w-5 h-5 accent-fc-red flex-shrink-0 mt-1"
                      data-testid={`pref-${opt.k}`}
                    />
                  </label>
                ))}
                <button onClick={savePrefs} className="btn-fc-red text-xs mt-4" data-testid="prefs-save">
                  <Check size={12} /> Save Preferences
                </button>
              </div>
            )}

            {tab === "privacy" && (
              <div className="bg-white border border-gray-200 p-6 space-y-4" data-testid="privacy-form">
                <p className="fc-overline text-[10px]">PRIVACY</p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  We never show your real name in Community, Internal, Leaderboard, or Certificates. Your alias is{" "}
                  <span className="font-mono text-fc-red font-bold">{profile.public_alias}</span>.
                </p>
                <label className="flex items-center justify-between cursor-pointer py-2">
                  <span className="text-sm">Show your alias on the Leaderboard</span>
                  <input
                    type="checkbox"
                    checked={!!privacy.show_alias_on_leaderboard}
                    onChange={(e) => setPrivacy(p => ({ ...p, show_alias_on_leaderboard: e.target.checked }))}
                    className="w-4 h-4 accent-fc-red"
                    data-testid="privacy-show-alias"
                  />
                </label>
                <button onClick={savePrivacy} className="btn-fc-red text-xs" data-testid="privacy-save">
                  <Check size={12} /> Save Privacy
                </button>
              </div>
            )}

            {tab === "membership" && (
              <div className="bg-white border border-gray-200 p-6 space-y-4" data-testid="membership-card">
                <p className="fc-overline text-[10px]">MEMBERSHIP</p>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-2xl font-black tracking-tight uppercase">
                      {profile.membership_type === "none" ? "Free" : profile.membership_type}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Role: {profile.role}</p>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {profile.membership_type === "none" ? (
                      <button onClick={() => navigate("/pricing")} className="btn-fc-red" data-testid="manage-membership-cta">
                        Choose Plan
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={async () => {
                            try {
                              const r = await api.post("/billing/portal");
                              if (r.data?.url) {
                                window.location.href = r.data.url;
                              } else {
                                toast.error("Portal not available");
                              }
                            } catch (e) {
                              console.error("billing portal", e);
                              toast.error(e.response?.data?.detail || "Could not open billing portal");
                            }
                          }}
                          className="btn-fc-red"
                          data-testid="open-billing-portal"
                        >
                          Manage Billing
                        </button>
                        <button onClick={() => navigate("/pricing")} className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500 hover:text-fc-red" data-testid="change-plan-cta">
                          Change Plan
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-[10px] tracking-wider text-gray-400">
                  Billing managed securely by Stripe. Update card, view invoices, cancel anytime.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
